"use client";

import { ChevronDown } from "lucide-react";
import { useState } from "react";

export interface SelectOption {
  value: string | number;
  name: string;
}

interface SelectProps {
  label: string;
  options: SelectOption[];
  value?: string | number;
  onChange?: (option: SelectOption) => void;
  placeholder?: string;
  className?: string;
}

export default function Select({
  label,
  options,
  value,
  onChange,
  placeholder,
  className = "",
}: Readonly<SelectProps>) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = options?.find((o) => o.value === value);

  return (
    <div
      dir="rtl"
      className={`relative w-full min-w-0 ${className}`}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {/* Trigger */}
      <button
        type="button"
        className={`
          flex w-full items-center justify-start gap-1.5
          text-sm font-medium
          text-gray-600 hover:text-gray-800
          transition-colors
        `}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        <span
          className={`truncate ${
            selectedOption ? "text-gray-900" : "text-inp text-base font-normal"
          }`}
        >
          {selectedOption?.name ?? (placeholder || label)}
        </span>

        <ChevronDown
          size={16}
          className={`shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown */}
      <div
        className={`
          absolute top-full right-0 pt-1.5 z-50
          transition-all duration-200 origin-top-right
          ${
            isOpen
              ? "opacity-100 scale-100"
              : "opacity-0 scale-95 pointer-events-none"
          }
        `}
      >
        <div
          className={`
            bg-white rounded-xl shadow-lg
            border border-gray-200
            py-1.5 min-w-35
            text-right
          `}
        >
          {options?.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange?.(option);
                setIsOpen(false);
              }}
              className={`
                w-full px-4 py-2 text-sm
                text-right
                text-gray-700 hover:bg-blue-50
                transition-colors
                ${value === option.value ? "bg-blue-50 font-medium" : ""}
              `}
            >
              {option.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
